import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmt, pct } from "@/lib/format";
import { KLayout, KDemo } from "@/pages/kreonis/KLayout";
import { Seo } from "@/components/Seo";

function Spark({ seed, up }) {
  const pts = useMemo(() => {
    let s = 0; for (const c of seed) s = (s * 31 + c.charCodeAt(0)) % 9973;
    const rand = () => ((s = (s * 1103515245 + 12345) % 2147483648) / 2147483648);
    let v = 20; const arr = [];
    for (let i = 0; i < 24; i++) { v += (rand() - (up ? 0.42 : 0.58)) * 6; arr.push(Math.max(2, Math.min(38, v))); }
    return arr.map((y, i) => `${(i / 23) * 100},${40 - y}`).join(" ");
  }, [seed, up]);
  return <svg viewBox="0 0 100 40" className="h-8 w-24" preserveAspectRatio="none"><polyline points={pts} fill="none" stroke={up ? "#10b981" : "#f43f5e"} strokeWidth="2" /></svg>;
}

const biasCls = (b) => b === "bullish" ? "text-emerald-400" : b === "bearish" ? "text-rose-400" : "text-slate-400";

export default function KMarkets() {
  const [assets, setAssets] = useState([]);
  useEffect(() => { api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {}); }, []);
  return (
    <KLayout>
      <Seo title="KREONIS — Global Asset Class Matrix" />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="flex items-center justify-between"><div><h1 className="font-display text-3xl font-bold text-slate-50">Global Asset Class Matrix</h1><p className="mt-2 text-slate-400">Crypto · Forex · Indices · Commodities — multi-timeframe structure & AI bias.</p></div><KDemo /></div>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/50">
          <table className="w-full text-sm" data-testid="kmarkets-table">
            <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Asset</th><th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3">Structure</th><th className="px-4 py-3">Trend Strength</th><th className="px-4 py-3 text-right">Volatility</th><th className="px-4 py-3 text-right">Support / Resistance</th><th className="px-4 py-3 text-right">AI Bias</th>
            </tr></thead>
            <tbody>
              {assets.map((a) => {
                const up = a.ai_bias !== "bearish";
                const strength = Math.min(100, Math.round(Math.abs(a.change_pct) * 12 + (a.ai_bias === "neutral" ? 20 : 55)));
                return (
                  <tr key={a.symbol} className="border-b border-slate-800/70 last:border-0 hover:bg-slate-800/40" data-testid={`kmarket-${a.symbol.replace(/\W/g, "-").toLowerCase()}`}>
                    <td className="px-4 py-3"><div className="font-display font-semibold text-slate-100">{a.symbol}</div><div className="text-xs capitalize text-slate-500">{a.market}</div></td>
                    <td className="px-4 py-3 text-right font-mono text-slate-100">{fmt(a.price)}<div className={`text-xs ${a.change_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct(a.change_pct)}</div></td>
                    <td className="px-4 py-3"><Spark seed={a.symbol} up={up} /></td>
                    <td className="px-4 py-3"><div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-800"><div className={`h-full ${up ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${strength}%` }} /></div><span className="text-[10px] text-slate-500">{strength}/100</span></td>
                    <td className="px-4 py-3 text-right capitalize text-slate-300">{a.volatility}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-slate-400">{fmt(a.price * 0.97)} / {fmt(a.price * 1.03)}</td>
                    <td className={`px-4 py-3 text-right font-semibold capitalize ${biasCls(a.ai_bias)}`}>{a.ai_bias}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </KLayout>
  );
}
